<template>
  <nav
    ref="sidebarNavRef"
    class="h-full bg-sidebar border-r border-sidebar flex flex-col"
    role="navigation"
    :aria-label="ariaLabel"
  >
    <!-- Sidebar Header -->
    <div
      class="flex-shrink-0 border-b border-sidebar"
      :class="[
        collapsed ? 'px-2 py-3 flex flex-col items-center' : 'p-4',
        { 'relative z-50': supersearchSidebarEnabled && !collapsed && showSupersearchPanel }
      ]"
    >
      <!-- Logo Row (design: w-8 h-8 logo area) -->
      <div :class="collapsed ? 'mb-1' : 'mb-3'">
        <BaseLogoLink 
          :show-text="!collapsed"
          size="small"
          variant="default"
        >
          <template #trailing>
            <BaseTooltip
              v-if="appEnv && !collapsed"
              :content="appEnv.tooltip"
              position="bottom"
            >
              <BaseBadge variant="yellow">{{ appEnv.badgeText }}</BaseBadge>
            </BaseTooltip>
          </template>
        </BaseLogoLink>
      </div>
      <!-- Env badge under logo when collapsed -->
      <div v-if="collapsed && appEnv" class="mb-2 flex justify-center px-1">
        <BaseTooltip :content="appEnv.tooltip" position="right">
          <BaseBadge variant="yellow">{{ appEnv.badgeText }}</BaseBadge>
        </BaseTooltip>
      </div>
      
      <!-- Action Buttons: one row when expanded (Bug Report + Theme); Remix icons, theme colors -->
      <div class="w-full" :class="collapsed ? 'flex flex-col items-center gap-0.5' : 'flex items-center gap-2'">
        <!-- Bug Report Button -->
        <BaseTooltip v-if="collapsed" content="Bug Report" position="right">
          <BaseButton
            @click="showBugReportForm = true"
            variant="ghost"
            size="small"
            class="p-2.5 w-full justify-center text-dark-text-secondary hover:bg-surface-hover"
            aria-label="Bug Report"
          >
            <i class="ri-bug-line w-5 h-5 text-lg flex items-center justify-center"></i>
            <span v-if="!collapsed">Bug Report</span>
          </BaseButton>
        </BaseTooltip>
        <BaseButton
          v-else
          @click="showBugReportForm = true"
          variant="ghost"
          size="small"
          class="whitespace-nowrap text-dark-text-secondary hover:bg-surface-hover flex items-center gap-2"
          aria-label="Bug Report"
        >
          <i class="ri-bug-line w-5 h-5 text-lg flex-shrink-0 mr-0 leading-none inline-flex items-center justify-center"></i>
          <span>Bug Report</span>
        </BaseButton>
        
        <!-- Theme Toggle Button -->
        <BaseTooltip v-if="collapsed" :content="isDark ? 'Switch to light mode' : 'Switch to dark mode'" position="right">
          <BaseButton
            @click="toggleTheme"
            variant="ghost"
            size="small"
            class="p-2.5 w-full justify-center text-dark-text-secondary hover:bg-surface-hover"
            :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
          >
            <i :class="isDark ? 'ri-sun-line' : 'ri-moon-line'" class="w-5 h-5 text-lg flex items-center justify-center"></i>
          </BaseButton>
        </BaseTooltip>
        <BaseButton
          v-else
          @click="toggleTheme"
          variant="ghost"
          size="small"
          class="whitespace-nowrap text-dark-text-secondary hover:bg-surface-hover flex items-center gap-2"
          :aria-label="isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <i :class="[isDark ? 'ri-sun-line' : 'ri-moon-line', 'w-5 h-5 text-lg flex-shrink-0 mr-0 leading-none inline-flex items-center justify-center']"></i>
          <span>{{ isDark ? 'Light' : 'Dark' }}</span>
        </BaseButton>

        <!-- Search icon when collapsed: hover opens flyout with search bar + results -->
        <div
          v-if="supersearchSidebarEnabled && collapsed"
          class="w-full"
          @mouseenter="handleSearchFlyoutEnter"
          @mouseleave="handleSearchFlyoutLeave"
        >
          <BaseTooltip content="Search" position="right">
            <BaseButton
              variant="ghost"
              size="small"
              class="p-2.5 w-full justify-center cursor-pointer text-dark-text-secondary hover:bg-surface-hover"
              aria-label="Search"
              @click="openSearchFlyout"
            >
              <i class="ri-search-line w-5 h-5 text-lg flex items-center justify-center" aria-hidden="true"></i>
            </BaseButton>
          </BaseTooltip>
        </div>
      </div>
      
      <!-- Super Search Input (design: bg-dark-surface2 border white/8 rounded-lg text-sm, icon ri-search-line left-3) -->
      <div v-if="supersearchSidebarEnabled && !collapsed" class="mt-3 relative" ref="supersearchContainerRef">
        <BaseInput
          v-model="supersearchQuery"
          type="search"
          placeholder="Search across everything…"
          class="w-full supersearch-input"
          @keydown.escape="handleSupersearchEscape"
          @keydown.enter="handleSupersearchSubmit"
          @update:model-value="handleSupersearchInput"
          @focus="handleSupersearchFocus"
          @blur="handleSupersearchBlur"
        />
        <i class="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-dark-text-muted pointer-events-none" aria-hidden="true"></i>
        <BaseIconButton
          v-if="supersearchQuery"
          size="xs"
          variant="ghost"
          class="absolute right-2 top-1/2 -translate-y-1/2 z-10 sidebar-clear-search-btn text-dark-text-muted hover:text-dark-text-primary"
          @click="handleSupersearchClear"
          aria-label="Clear search"
        >
          <XIcon />
        </BaseIconButton>
        
        <SupersearchQuickPanel
          v-if="showSupersearchPanel"
          :query="supersearchQuery"
          :results="supersearchResults"
          :loading="supersearchLoading"
          @close="hasSupersearchFocus = false"
          @selecting-start="isSelectingResult = true"
          @selecting-end="isSelectingResult = false"
        />
      </div>
    </div>

    <!-- Fixed: OTS KPI + Expand/Collapse (does not scroll) -->
    <div v-if="!collapsed" class="flex-shrink-0 px-3 pt-3">
      <!-- OTS KPI: click opens same OTS KPI panel (trigger slot, no caret) -->
      <OtsKpiPopover :kpi="printIqKpiStore.data" :population="printIqKpiStore.population" @update:population="printIqKpiStore.setPopulation">
        <template #trigger="{ open }">
          <button
            type="button"
            class="w-full flex flex-col gap-0.5 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover min-w-0 cursor-pointer"
            :class="{ 'bg-surface-muted': open }"
            :aria-expanded="open"
            :aria-label="`${printIqKpiStore.data?.date_from ?? 'OTS'} KPI`"
          >
            <!-- Row 1: OTS label left, Total XX.XX% right -->
            <div class="flex items-center gap-2 min-w-0 w-full text-xs">
              <span class="font-medium text-text-secondary shrink-0">On Time Shipping</span>
              <span v-if="printIqKpiStore.loading" class="truncate text-text-muted">—</span>
              <span v-else-if="!printIqKpiStore.available" class="truncate text-text-muted"> N/A</span>
              <span v-else-if="printIqKpiStore.data" class="inline-flex items-center gap-x-1 min-w-0">
                <span :class="otsPctClass(printIqKpiStore.data.TOTAL_OTS_Pct)" class="font-semibold shrink-0">{{ otsFormatPct(printIqKpiStore.data.TOTAL_OTS_Pct) }}</span>
                <OtsTrendDelta :delta="printIqKpiStore.data.trend?.TOTAL_OTS_Pct" :vs-date="printIqKpiStore.data.trend?.previous_date" size="sm" arrow-only />
              </span>
            </div>
            <!-- Row 2: EVT XX.XX% | LAN XX.XX% -->
            <div v-if="printIqKpiStore.data" class="flex flex-nowrap items-center gap-x-1.5 gap-y-0 truncate min-w-0 text-xs">
              <span class="text-text-secondary shrink-0">EVT</span>
              <span :class="otsPctClass(printIqKpiStore.data.EVT_OTS_Pct)" class="font-semibold shrink-0">{{ otsFormatPct(printIqKpiStore.data.EVT_OTS_Pct) }}</span>
              <OtsTrendDelta :delta="printIqKpiStore.data.trend?.EVT_OTS_Pct" :vs-date="printIqKpiStore.data.trend?.previous_date" size="sm" arrow-only />
              <span class="text-text-muted shrink-0" aria-hidden="true">|</span>
              <span class="text-text-secondary shrink-0">LAN</span>
              <span :class="otsPctClass(printIqKpiStore.data.LAN_OTS_Pct)" class="font-semibold shrink-0">{{ otsFormatPct(printIqKpiStore.data.LAN_OTS_Pct) }}</span>
              <OtsTrendDelta :delta="printIqKpiStore.data.trend?.LAN_OTS_Pct" :vs-date="printIqKpiStore.data.trend?.previous_date" size="sm" arrow-only />
            </div>
          </button>
        </template>
      </OtsKpiPopover>

      <div
        v-if="hasDisclosureSidebarSections"
        class="mt-2 flex w-full text-xs border-b border-border"
      >
        <button
          type="button"
          class="flex-1 py-1.5 text-center text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer"
          @click="expandAllSidebarFolders"
        >
          Expand all
        </button>
        <button
          type="button"
          class="flex-1 py-1.5 text-center text-text-muted hover:text-text hover:bg-surface-hover transition-colors cursor-pointer border-l border-border"
          @click="collapseAllSidebarFolders"
        >
          Collapse all
        </button>
      </div>
    </div>

    <!-- Navigation Content: scrollable nav items only -->
    <BaseScrollbar
      v-if="!collapsed"
      :horizontal="false"
      :vertical="true"
      :options="{
        wheelSpeed: 1,
        wheelPropagation: false,
        swipeEasing: true,
        suppressScrollX: true,
        suppressScrollY: false
      }"
      :class="['flex-1', { 'relative z-0': supersearchSidebarEnabled && showSupersearchPanel }]"
    >
      <div class="px-3 py-2 space-y-0.5">
        <NavigationTreeRenderer :items="filteredRootSections" />
      </div>
    </BaseScrollbar>

    <!-- Mini nav (icons only) when collapsed -->
    <template v-else>
      <BaseScrollbar
        :horizontal="false"
        :vertical="true"
        :reserve-rail-space="false"
        :options="{ wheelSpeed: 1, wheelPropagation: false, suppressScrollX: true, suppressScrollY: false }"
        class="flex-1 min-h-0"
      >
        <div class="py-1 flex flex-col items-center gap-0 w-full min-w-0">
          <!-- OTS KPI (collapsed): compact stack; click opens same panel -->
          <div class="relative flex items-center justify-center min-w-0 py-1.5">
            <OtsKpiPopover :kpi="printIqKpiStore.data" :population="printIqKpiStore.population" @update:population="printIqKpiStore.setPopulation">
              <template #trigger="{ open }">
                <button
                  type="button"
                  class="flex flex-col items-center justify-center p-2 rounded-md text-text hover:bg-surface-hover transition-colors w-full min-w-0 shrink-0 cursor-pointer min-h-[2.75rem]"
                  :class="{ 'bg-surface-muted': open }"
                  :aria-expanded="open"
                  aria-label="OTS KPI"
                >
                  <span class="text-xs font-medium text-text-secondary">OTS</span>
                  <span v-if="printIqKpiStore.loading || !printIqKpiStore.available" class="text-xs font-semibold text-text-muted">—</span>
                  <span v-else-if="printIqKpiStore.data" :class="['text-xs font-semibold', otsPctClass(printIqKpiStore.data.TOTAL_OTS_Pct)]">{{ otsFormatPct(printIqKpiStore.data.TOTAL_OTS_Pct) }}</span>
                </button>
              </template>
            </OtsKpiPopover>
          </div>
          <template v-for="section in filteredRootSections" :key="section.key">
            <div class="relative flex items-center justify-center w-full min-w-0 py-1.5">
              <BaseTooltip v-if="section.item_type === 'external'" :content="section.display_label" position="right">
                <a
                  :href="section.external_url ?? '#'"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-center p-2 rounded-md text-text hover:bg-surface-hover transition-colors w-full min-w-0 shrink-0 cursor-pointer"
                  :class="{ 'bg-surface-muted': isSectionActive(section) }"
                  :aria-label="`${section.display_label} (opens in a new tab)`"
                >
                  <BaseSidebarSectionIcon :section-id="section.key" />
                </a>
              </BaseTooltip>
              <BaseTooltip v-else :content="section.display_label" position="right">
                <button
                  type="button"
                  class="flex items-center justify-center p-2 rounded-md text-text hover:bg-surface-hover transition-colors w-full min-w-0 shrink-0 cursor-pointer"
                  :class="{ 'bg-surface-muted': isSectionActive(section) || flyoutSectionKey === section.key }"
                  :aria-label="section.display_label"
                  :aria-expanded="flyoutSectionKey === section.key"
                  @click="handleMiniSectionClick(section.key, $event)"
                >
                  <BaseSidebarSectionIcon :section-id="section.key" />
                </button>
              </BaseTooltip>
            </div>
          </template>
        </div>
      </BaseScrollbar>

      <!-- Flyout panel (Teleport): same NavigationTreeRenderer and permissions as expanded sidebar -->
      <Teleport to="body">
        <Transition name="flyout">
          <div
            v-if="flyoutSectionKey"
            ref="flyoutRef"
            class="fixed z-[100] min-w-[12rem] max-w-[20rem] max-h-[min(80vh,400px)] rounded-lg border border-border bg-surface shadow-lg overflow-hidden flex flex-col"
            :style="flyoutStyle"
          >
            <div class="flex-shrink-0 px-3 pt-2 pb-1 border-b border-border">
              <h3 class="text-sm font-semibold text-dark-text-primary">{{ flyoutSectionLabel }}</h3>
            </div>
            <BaseScrollbar
              class="flex-1 min-h-0"
              max-height="calc(min(80vh, 400px) - 3.5rem)"
              :options="{ wheelSpeed: 1, wheelPropagation: false, suppressScrollX: true, suppressScrollY: false }"
            >
              <div class="px-3 py-2">
                <NavigationTreeRenderer
                  :items="flyoutSectionChildren"
                  :depth="1"
                  flyout
                />
              </div>
            </BaseScrollbar>
          </div>
        </Transition>
      </Teleport>

      <!-- Search flyout (hover/click search icon when collapsed): same block as expanded sidebar -->
      <Teleport to="body">
        <Transition name="flyout">
          <div
            v-if="supersearchSidebarEnabled && searchFlyoutOpen"
            ref="searchFlyoutRef"
            class="fixed z-[100] w-[min(24rem,calc(100vw-88px))] max-h-[min(80vh,480px)] flex flex-col rounded-lg border border-border bg-surface shadow-lg overflow-hidden"
            :style="searchFlyoutStyle"
            @mouseenter="handleSearchFlyoutPanelEnter"
            @mouseleave="handleSearchFlyoutLeave"
          >
            <div class="flex-shrink-0 px-3 pt-2 pb-1 border-b border-border">
              <h3 class="text-sm font-semibold text-dark-text-primary">Search</h3>
            </div>
            <div class="p-3 flex flex-col overflow-hidden min-h-0 flex-1">
              <div class="relative flex-shrink-0">
                <BaseInput
                  ref="searchFlyoutInputRef"
                  v-model="supersearchQuery"
                  type="search"
                  placeholder="Search across everything…"
                  class="w-full supersearch-input"
                  @keydown.escape="handleSearchFlyoutEscape"
                  @keydown.enter="handleSupersearchSubmit"
                  @update:model-value="handleSupersearchInput"
                />
                <i class="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-dark-text-muted pointer-events-none" aria-hidden="true"></i>
                <BaseIconButton
                  v-if="supersearchQuery"
                  size="xs"
                  variant="ghost"
                  class="absolute right-2 top-1/2 -translate-y-1/2 z-10 sidebar-clear-search-btn text-dark-text-muted hover:text-dark-text-primary"
                  @click="handleSearchFlyoutClear"
                  aria-label="Clear search"
                >
                  <XIcon />
                </BaseIconButton>
              </div>
              <BaseScrollbar
                ref="searchFlyoutScrollbarRef"
                class="flex-1 min-h-0 mt-2 overflow-hidden"
                max-height="min(80vh, 400px)"
                :options="{ wheelSpeed: 1, wheelPropagation: false, suppressScrollX: true, suppressScrollY: false }"
              >
                <SupersearchQuickPanel
                  v-if="showSupersearchPanelInFlyout"
                  :query="supersearchQuery"
                  :results="supersearchResults"
                  :loading="supersearchLoading"
                  inline
                  @close="closeSearchFlyout"
                  @selecting-start="isSelectingResult = true"
                  @selecting-end="isSelectingResult = false"
                />
              </BaseScrollbar>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- Org Chart icon when collapsed (bottom of nav, matches expanded position above footer) -->
      <div
        v-if="orgChartNavVisible"
        class="flex-shrink-0 w-full border-t border-border py-1.5 flex justify-center"
      >
        <BaseTooltip content="Org Chart" position="right">
          <RouterLink
            to="/org-chart"
            class="flex items-center justify-center p-2 rounded-md text-text hover:bg-surface-hover transition-colors w-full min-w-0 shrink-0 cursor-pointer"
            :class="{ 'bg-surface-muted': isActiveRoute('/org-chart') }"
            aria-label="Org Chart"
          >
            <BaseIcon size="large" variant="ghost">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </BaseIcon>
          </RouterLink>
        </BaseTooltip>
      </div>
    </template>

    <!-- Org Chart Link (expanded sidebar only) -->
    <div v-if="!collapsed && orgChartNavVisible" class="w-full border-t border-border">
      <div class="px-4 py-2">
        <BaseDisclosureItem 
        to="/org-chart"
        :active="isActiveRoute('/org-chart')"
      >
        <template #icon>
          <BaseIcon size="small" variant="ghost">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </BaseIcon>
        </template>
        <template #content>
          <span>Org Chart</span>
        </template>
      </BaseDisclosureItem>
      </div>
    </div>

    <!-- Sidebar Footer -->
    <div class="sidebar-footer border-t border-border flex-shrink-0 bg-sidebar-footer h-[53px] flex items-center" :class="collapsed ? 'px-2 justify-center' : 'px-4'">
      <BaseMenu
        :model-value="userMenuOpen"
        @update:model-value="userMenuOpen = $event"
        position="top"
        :items="userMenuItems"
        full-width
        :append-to="collapsed ? 'body' : undefined"
        @item-click="handleUserMenuClick"
      >
        <template #trigger>
          <div 
            class="user-profile-button"
            :class="{ 'user-profile-button--collapsed': collapsed }"
            :aria-expanded="userMenuOpen"
            :aria-label="userMenuOpen ? 'Close user menu' : 'Open user menu'"
            role="button"
            tabindex="0"
          >
            <BaseCircularBadge
              :text="currentUser.name"
              :size="'medium'"
              variant="blue"
              :show-text="false"
            />
            <div v-if="!collapsed" class="user-info">
              <p class="text-sm font-medium text-dark-text-primary user-name">{{ currentUser.name }}</p>
              <p class="text-xs text-dark-text-muted user-email">{{ currentUser.email }}</p>
            </div>
            <BaseIcon v-if="!collapsed" size="small" variant="ghost" class="user-chevron">
              <ChevronDownIcon />
            </BaseIcon>
          </div>
        </template>
      </BaseMenu>
    </div>

    <!-- Bug Report Form Modal -->
    <BugReportForm
      v-model="showBugReportForm"
      @submitted="handleBugReportSubmitted"
    />
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, provide, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useNavigationStore } from '@/stores/navigationStore'
import { useDdQueueStore } from '@/stores/ddQueueStore'
import { usePrintIqKpiStore } from '@/stores/printiqKpi'
import { useToast } from 'vue-toastification'
import { useSupersearch } from '@/composables/useSupersearch'
import { useAppEnv } from '@/composables/useAppEnv'
import { useDdPermitSync } from '@/composables/useDdPermitSync'
import { useNavigationConfig } from '@/composables/useNavigationConfig'
import BaseDisclosureItem from '@/components/ui/BaseDisclosureItem.vue'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import BaseCircularBadge from '@/components/ui/BaseCircularBadge.vue'
import BaseLogoLink from '@/components/ui/BaseLogoLink.vue'
import BaseBadge from '@/components/feedback/BaseBadge.vue'
import BaseTooltip from '@/components/feedback/BaseTooltip.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseIconButton from '@/components/navigation/BaseIconButton.vue'
import BaseMenu from '@/components/navigation/BaseMenu.vue'
import BaseInput from '@/components/forms/BaseInput.vue'
import BugReportForm from '@/components/bug-reports/BugReportForm.vue'
import SupersearchQuickPanel from '@/components/supersearch/SupersearchQuickPanel.vue'
import BaseScrollbar from '@/components/layout/BaseScrollbar.vue'
import BaseSidebarSectionIcon from '@/components/layout/BaseSidebarSectionIcon.vue'
import NavigationTreeRenderer from '@/components/layout/NavigationTreeRenderer.vue'
import OtsKpiPopover from '@/components/layout/OtsKpiPopover.vue'
import OtsTrendDelta from '@/components/operations/OtsTrendDelta.vue'
import { X } from 'lucide-vue-next'
import FolderIcon from '../icons/FolderIcon.vue'
import {
  createDefaultSidebarFolderState,
  readSidebarFolderPreference,
  writeSidebarFolderPreference,
  type SidebarFolderState,
} from '@/utils/sidebarFolderPreference'
import {
  collectOpenFolderKeysForRoute,
  filterNavigationTree,
  isRouteInNavigationSubtree,
} from '@/utils/navigationTreeUtils'
import type { NavigationItem } from '@/types/navigation'

const XIcon = X
import DocumentIcon from '../icons/DocumentIcon.vue'
import SettingsIcon from '../icons/SettingsIcon.vue'
import LogoutIcon from '../icons/LogoutIcon.vue'
import ChevronDownIcon from '../icons/ChevronDownIcon.vue'

const authStore = useAuthStore()
const navigationStore = useNavigationStore()
const ddQueueStore = useDdQueueStore()
const { permit: ddPermit } = storeToRefs(ddQueueStore)
const { loadNavigation } = useNavigationConfig()
useDdPermitSync()

/** Org chart nav: show when route is registered and user is not an external client. */
const orgChartNavVisible = computed(() => {
  if (currentUser.value.isExternalClient) {
    return false
  }
  return router.hasRoute('OrgChart')
})

// Props
interface User {
  name: string
  email: string
  initials: string
  isExternalClient: boolean
}

interface Props {
  collapsed?: boolean
  ariaLabel?: string
  user?: User
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  ariaLabel: 'Primary navigation'
})

// Router
const route = useRoute()
const router = useRouter()

// Auth store and toast
const toast = useToast()
const printIqKpiStore = usePrintIqKpiStore()

const filteredRootSections = computed(() =>
  filterNavigationTree(
    navigationStore.items,
    {
      hasPermission: (permission) => authStore.hasPermission(permission),
      ddPermit: ddPermit.value,
    },
    true,
  ),
)

// OTS percentage display: same thresholds/colors as OTS page KPI bar and OtsKpiPopover (>98 green, 90–98 yellow, <90 red)
function otsFormatPct(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'N/A'
  return typeof pct === 'number' && !Number.isNaN(pct) ? pct.toFixed(2) + '%' : '—'
}
function otsPctClass(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'text-green-600 dark:text-green-400'
  if (typeof pct !== 'number' || Number.isNaN(pct)) return 'text-text-muted'
  if (pct > 98) return 'text-green-600 dark:text-green-400'
  if (pct >= 90) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

// Folder state management - per-user localStorage with legacy migration
const loadFolderState = (): SidebarFolderState => {
  const stored = readSidebarFolderPreference(authStore.user?.uuid)
  return stored ?? createDefaultSidebarFolderState()
}

const openFolders = ref<Record<string, boolean>>(loadFolderState())
provide('sidebarOpenFolders', openFolders)
provide('sidebarNavDense', true)

function setAllSidebarFolders(open: boolean): void {
  const next = { ...openFolders.value }
  for (const key of Object.keys(next)) {
    next[key as keyof SidebarFolderState] = open
  }
  openFolders.value = next
}

function expandAllSidebarFolders(): void {
  setAllSidebarFolders(true)
}

function collapseAllSidebarFolders(): void {
  setAllSidebarFolders(false)
}

// Mini sidebar flyout state (collapsed mode only)
const flyoutSectionKey = ref<string | null>(null)
const flyoutRef = ref<HTMLElement | null>(null)
const sidebarNavRef = ref<HTMLElement | null>(null)
const flyoutTop = ref(80)

// Search flyout (collapsed sidebar: hover over search icon)
const searchFlyoutOpen = ref(false)
const searchFlyoutOpenTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const searchFlyoutCloseTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const searchFlyoutRef = ref<HTMLElement | null>(null)
const searchFlyoutScrollbarRef = ref<InstanceType<typeof BaseScrollbar> | null>(null)
const searchFlyoutInputRef = ref<HTMLElement | null>(null)
const SEARCH_FLYOUT_OPEN_DELAY_MS = 120
const SEARCH_FLYOUT_CLOSE_DELAY_MS = 150
const searchFlyoutTop = ref(80)

// Auto-open folders when current route matches navigation tree
watch(
  () => [route.path, filteredRootSections.value] as const,
  ([path]) => {
    const keysToOpen = collectOpenFolderKeysForRoute(filteredRootSections.value, path)
    if (!keysToOpen.length) return

    const next = { ...openFolders.value }
    for (const key of keysToOpen) {
      next[key] = true
    }
    openFolders.value = next
  },
  { immediate: true },
)

// Save folder state to localStorage whenever it changes
watch(
  openFolders,
  (newState) => {
    writeSidebarFolderPreference(authStore.user?.uuid, newState as SidebarFolderState)
  },
  { deep: true },
)

// Close flyouts on route change
watch(() => route.path, () => {
  flyoutSectionKey.value = null
  searchFlyoutOpen.value = false
})

// Click-outside to close section flyout (attach after open so opening click doesn't close)
watch(flyoutSectionKey, (key) => {
  if (key) {
    nextTick(() => {
      setTimeout(() => document.addEventListener('click', handleClickOutsideFlyout), 0)
    })
  } else {
    document.removeEventListener('click', handleClickOutsideFlyout)
  }
})

// Escape closes flyout
function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    flyoutSectionKey.value = null
    if (searchFlyoutOpen.value) {
      searchFlyoutOpen.value = false
    }
  }
}
const KPI_REFRESH_MS = 10 * 60 * 1000
let kpiRefreshTimerId: ReturnType<typeof setTimeout> | null = null
function scheduleKpiRefresh() {
  if (kpiRefreshTimerId != null) {
    clearTimeout(kpiRefreshTimerId)
    kpiRefreshTimerId = null
  }
  kpiRefreshTimerId = setTimeout(() => {
    kpiRefreshTimerId = null
    printIqKpiStore.fetchKpi().then(() => {
      if (printIqKpiStore.available) scheduleKpiRefresh()
    })
  }, KPI_REFRESH_MS)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  if (authStore.isAuthenticated) {
    loadNavigation().catch(() => {
      if (!navigationStore.items.length) {
        toast.error('Failed to load navigation')
      }
    })
  }
  if (printIqKpiStore.isStale || !printIqKpiStore.data) {
    printIqKpiStore.fetchKpi().then(() => {
      if (printIqKpiStore.available && printIqKpiStore.fetchedAt != null) {
        const delay = Math.max(0, KPI_REFRESH_MS - (Date.now() - printIqKpiStore.fetchedAt))
        if (delay > 0) {
          kpiRefreshTimerId = setTimeout(() => {
            kpiRefreshTimerId = null
            printIqKpiStore.fetchKpi().then(() => {
              if (printIqKpiStore.available) scheduleKpiRefresh()
            })
          }, delay)
        } else {
          scheduleKpiRefresh()
        }
      }
    })
  }
})
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('click', handleClickOutsideFlyout)
  if (searchFlyoutOpenTimer.value) clearTimeout(searchFlyoutOpenTimer.value)
  if (searchFlyoutCloseTimer.value) clearTimeout(searchFlyoutCloseTimer.value)
  if (searchFlyoutScrollbarUpdateTimeout.value) clearTimeout(searchFlyoutScrollbarUpdateTimeout.value)
  if (kpiRefreshTimerId != null) {
    clearTimeout(kpiRefreshTimerId)
    kpiRefreshTimerId = null
  }
})

// User menu state
const userMenuOpen = ref(false)
const isLoggingOut = ref(false)

// Theme and bug report state
const isDark = ref(document.documentElement.classList.contains('dark'))
const showBugReportForm = ref(false)
// App env badge: localhost → Local; staging → pill "Sandbox", tooltip "Sandbox Site" (yellow badge)
const appEnv = useAppEnv()

// Super Search — sidebar input hidden until ready for general release
const supersearchSidebarEnabled = false

const { query: supersearchQuery, setQuery: setSupersearchQuery, results: supersearchResults, loading: supersearchLoading, clear: clearSupersearch, search: performSupersearch } = useSupersearch()
const hasSupersearchFocus = ref(false)
const isSelectingResult = ref(false)
const supersearchContainerRef = ref<HTMLElement | null>(null)
const showSupersearchPanel = computed(() => {
  return hasSupersearchFocus.value && supersearchQuery.value.trim().length >= 2
})

const showSupersearchPanelInFlyout = computed(() => {
  return searchFlyoutOpen.value && supersearchQuery.value.trim().length >= 2
})

const searchFlyoutScrollbarUpdateTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
watch(
  [showSupersearchPanelInFlyout, supersearchLoading],
  ([panel, loading]) => {
    if (searchFlyoutScrollbarUpdateTimeout.value) {
      clearTimeout(searchFlyoutScrollbarUpdateTimeout.value)
      searchFlyoutScrollbarUpdateTimeout.value = null
    }
    if (!panel || loading) return
    const runUpdate = () => {
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            searchFlyoutScrollbarRef.value?.forceUpdate()
          })
        })
      })
    }
    runUpdate()
    searchFlyoutScrollbarUpdateTimeout.value = setTimeout(runUpdate, 150)
  },
  { flush: 'post' }
)

const handleSupersearchInput = (value: string | number | null) => {
  setSupersearchQuery(value == null ? '' : String(value))
}

const handleSupersearchSubmit = () => {
  // Prevent duplicate requests
  if (supersearchLoading.value) {
    return
  }
  if (supersearchQuery.value.trim().length >= 2) {
    // Trigger search immediately
    performSupersearch(5)
  }
}

const handleSupersearchEscape = () => {
  clearSupersearch()
  hasSupersearchFocus.value = false
}

const handleSupersearchClear = () => {
  clearSupersearch()
  hasSupersearchFocus.value = false
}

const handleSupersearchFocus = () => {
  hasSupersearchFocus.value = true
}

const handleSupersearchBlur = (event: FocusEvent) => {
  // Use setTimeout to check focus state after blur event completes
  // This allows us to see if focus returned to the input or moved to the panel
  setTimeout(() => {
    // Check if focus moved to something inside the search container (input, buttons, or panel)
    const activeElement = document.activeElement as HTMLElement | null
    const relatedTarget = event.relatedTarget as HTMLElement | null
    
    // Check if the currently focused element is within our search container
    const isFocusInContainer = activeElement && supersearchContainerRef.value?.contains(activeElement)
    
    // Check if the element receiving focus (relatedTarget) is within our search container
    const isRelatedTargetInContainer = relatedTarget && supersearchContainerRef.value?.contains(relatedTarget)
    
    // Check if input is still focused by checking if activeElement is an input within our container
    const isInputFocused = activeElement?.tagName === 'INPUT' && 
                          activeElement.closest('.supersearch-input') !== null
    
    // Don't close if:
    // 1. Input is still focused (user is typing)
    // 2. Focus moved to something in the search container (panel, buttons, etc.)
    // 3. User is selecting a result
    if (isInputFocused || isFocusInContainer || isRelatedTargetInContainer || isSelectingResult.value) {
      // Keep panel open - focus is still in the search area
      return
    }
    
    // Focus truly left the search area - close the panel
    hasSupersearchFocus.value = false
  }, 0)
}

// Watch for route changes to close panel
watch(() => route.path, () => {
  hasSupersearchFocus.value = false
})

const toggleTheme = () => {
  document.documentElement.classList.add('no-transitions')
  isDark.value = !isDark.value
  document.documentElement.classList.toggle('dark', isDark.value)
  localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions')
    })
  })
}

const handleBugReportSubmitted = () => {
  // Bug report was submitted successfully
  showBugReportForm.value = false
  
  // If user is on the bug reports page, refresh it
  if (route.path === '/bug-reports') {
    // Dispatch a custom event to trigger refresh
    window.dispatchEvent(new CustomEvent('bug-report-submitted'))
  }
}

// Watch for theme changes from other components
let observer: MutationObserver | null = null

watch(() => props.collapsed, () => {
  // Re-check theme when sidebar state changes
  isDark.value = document.documentElement.classList.contains('dark')
}, { immediate: true })

onMounted(() => {
  // Watch for class changes on documentElement to sync theme
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        isDark.value = document.documentElement.classList.contains('dark')
      }
    })
  })

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

// Computed user from auth store (fallback to props for backwards compatibility)
const currentUser = computed(() => {
  if (authStore.user) {
    const nameParts = authStore.user.name?.split(' ') || []
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase()
      : nameParts[0]?.[0]?.toUpperCase() || '?'

    return {
      name: authStore.user.name || 'User',
      email: authStore.user.email || '',
      initials,
      isExternalClient: authStore.hasRole('external-contact'),
    }
  }

  return props.user || {
    name: 'Guest',
    email: '',
    initials: 'G',
    isExternalClient: false,
  }
})

// User menu items
const userMenuItems = computed(() => [
  {
    id: 'projects',
    label: 'My Projects',
    icon: FolderIcon,
    action: 'navigate',
    href: '/my/projects'
  },
  {
    id: 'notes',
    label: 'My Notes',
    icon: DocumentIcon,
    action: 'navigate',
    href: '/my/notes'
  },
  {
    type: 'separator'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon,
    action: 'navigate',
    href: '/settings/profile'
  },
  {
    id: 'logout',
    label: isLoggingOut.value ? 'Logging out...' : 'Sign out',
    icon: LogoutIcon,
    action: 'logout',
    variant: 'danger' as const,
    disabled: isLoggingOut.value
  }
])

const hasDisclosureSidebarSections = computed(() =>
  filteredRootSections.value.some((section) => section.item_type === 'section'),
)

const flyoutSection = computed(() =>
  filteredRootSections.value.find((section) => section.key === flyoutSectionKey.value) ?? null,
)

const flyoutSectionLabel = computed(() => flyoutSection.value?.display_label ?? flyoutSectionKey.value ?? '')

const flyoutSectionChildren = computed(() => flyoutSection.value?.children ?? [])

function isSectionActive(section: NavigationItem): boolean {
  return isRouteInNavigationSubtree(section, route.path)
}

const flyoutStyle = computed(() => {
  const top = Math.max(8, Math.min(flyoutTop.value, typeof window !== 'undefined' ? window.innerHeight - 420 : 400))
  return { left: '72px', top: `${top}px` }
})

function handleMiniSectionClick(sectionKey: string, e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  if (el) {
    const rect = el.getBoundingClientRect()
    flyoutTop.value = Math.max(8, Math.min(rect.top, (typeof window !== 'undefined' ? window.innerHeight : 800) - 420))
  }
  if (flyoutSectionKey.value === sectionKey) {
    flyoutSectionKey.value = null
  } else {
    flyoutSectionKey.value = sectionKey
  }
}

function handleClickOutsideFlyout(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (!flyoutSectionKey.value) return
  const flyoutEl = flyoutRef.value
  const sidebarEl = sidebarNavRef.value
  if (flyoutEl?.contains(target) || sidebarEl?.contains(target)) return
  flyoutSectionKey.value = null
}

const searchFlyoutStyle = computed(() => {
  const top = Math.max(8, Math.min(searchFlyoutTop.value, typeof window !== 'undefined' ? window.innerHeight - 500 : 400))
  return { left: '72px', top: `${top}px` }
})

function openSearchFlyout() {
  if (searchFlyoutCloseTimer.value) {
    clearTimeout(searchFlyoutCloseTimer.value)
    searchFlyoutCloseTimer.value = null
  }
  searchFlyoutOpen.value = true
  nextTick(() => focusSearchFlyoutInput())
}

function closeSearchFlyout() {
  searchFlyoutOpen.value = false
}

function handleSearchFlyoutEnter(e?: MouseEvent) {
  if (searchFlyoutCloseTimer.value) {
    clearTimeout(searchFlyoutCloseTimer.value)
    searchFlyoutCloseTimer.value = null
  }
  const el = e?.currentTarget as HTMLElement | undefined
  if (el) {
    const rect = el.getBoundingClientRect()
    searchFlyoutTop.value = Math.max(8, Math.min(rect.top, (typeof window !== 'undefined' ? window.innerHeight : 800) - 500))
  }
  searchFlyoutOpenTimer.value = setTimeout(() => {
    searchFlyoutOpenTimer.value = null
    searchFlyoutOpen.value = true
    nextTick(() => focusSearchFlyoutInput())
  }, SEARCH_FLYOUT_OPEN_DELAY_MS)
}

function handleSearchFlyoutLeave() {
  if (searchFlyoutOpenTimer.value) {
    clearTimeout(searchFlyoutOpenTimer.value)
    searchFlyoutOpenTimer.value = null
  }
  searchFlyoutCloseTimer.value = setTimeout(() => {
    searchFlyoutCloseTimer.value = null
    searchFlyoutOpen.value = false
  }, SEARCH_FLYOUT_CLOSE_DELAY_MS)
}

function handleSearchFlyoutPanelEnter() {
  if (searchFlyoutCloseTimer.value) {
    clearTimeout(searchFlyoutCloseTimer.value)
    searchFlyoutCloseTimer.value = null
  }
}

function handleSearchFlyoutEscape() {
  clearSupersearch()
  searchFlyoutOpen.value = false
}

function handleSearchFlyoutClear() {
  clearSupersearch()
}

function focusSearchFlyoutInput() {
  const comp = searchFlyoutInputRef.value as { $el?: HTMLElement } | null
  if (!comp?.$el) return
  const input = comp.$el.querySelector?.('input')
  if (input && typeof (input as HTMLInputElement).focus === 'function') (input as HTMLInputElement).focus()
}

// Methods
const isActiveRoute = (routePath: string): boolean => {
  // Exact match always works
  if (route.path === routePath) {
    return true
  }
  
  // For parent-child relationships, we need to be careful
  // If the current route starts with routePath + '/', it's a child route
  // But we should only highlight the parent if there's no more specific child route
  // that should be highlighted instead.
  
  // List of parent routes that have children in the sidebar
  // These should only match exactly, not when on their children
  const parentRoutesWithChildren = [
    '/reporting-dashboards',  // Has children: /sales, /notes, etc.
    '/crm',                    // Has children: /accounts, /sub-accounts, etc.
    '/product-dev/dashboard', // Activity dashboard (single day)
    '/product-dev/pim', // PIM products list + nested product routes
    '/product-dev/lists/universal-intake', // List + detail/edit under lists
    '/surveys',                // Has children: /dashboard, etc.
    '/inquiries',              // Has children: /dashboard, /public, etc.
    '/machines',                // Has children: /dashboard, /jobs
    '/sales-history'            // Has children: /list
  ]
  
  // If this is a parent route with children, only match exactly
  if (parentRoutesWithChildren.includes(routePath)) {
    return route.path === routePath
  }
  
  // For other routes, allow parent-child matching (for routes without explicit children)
  return route.path.startsWith(routePath + '/')
}

const handleUserMenuClick = async (item: any) => {
  if (item.action === 'logout') {
    // Prevent double-click
    if (isLoggingOut.value) return
    
    isLoggingOut.value = true
    
    try {
      // Call auth store logout (calls API and clears state)
      await authStore.logout()
      
      // Show success message
      toast.success('Logged out successfully')
      
      // Redirect to login
      router.push('/login')
    } catch (error) {
      console.error('Logout error:', error)
      
      // Even if API call fails, clear local state
      toast.error('Logout failed, but we cleared your session')
      authStore.clearAuth()
      router.push('/login')
    } finally {
      isLoggingOut.value = false
    }
  } else if (item.href) {
    // Navigate to the href
    router.push(item.href)
  }
}

// Auto-open folders removed - users can manually control folder state
// Folders remain in their current state (open/closed) regardless of navigation

</script>

<style scoped>
.flyout-enter-active,
.flyout-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.flyout-enter-from,
.flyout-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

.user-profile-button {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  background-color: transparent;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  cursor: pointer;
  
  text-align: left;
}

.user-profile-button--collapsed {
  justify-content: center;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name,
.user-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-chevron {
  flex-shrink: 0;
  
}

.user-profile-button[aria-expanded="true"] .user-chevron {
  transform: rotate(180deg);
}

/* Sidebar footer: center avatar and user info vertically */
.sidebar-footer :deep(.menu-wrapper) {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 100%;
}
.sidebar-footer :deep(.menu-wrapper > div:first-child) {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 100%;
}

/* Site Surveys / Reports nested disclosure - border via BaseSidebarSectionContent; layout only here */
.site-surveys-disclosure-wrapper :deep(button) {
  background-color: transparent !important;
  border-radius: 0;
}

.site-surveys-disclosure-wrapper :deep(button:hover) {
  background-color: transparent !important;
}

.site-surveys-disclosure-wrapper :deep(button[aria-expanded="true"]) {
  background-color: transparent !important;
}

.reports-disclosure-wrapper :deep(button) {
  background-color: transparent !important;
  border-radius: 0;
}

.reports-disclosure-wrapper :deep(button:hover) {
  background-color: transparent !important;
}

.reports-disclosure-wrapper :deep(button[aria-expanded="true"]) {
  background-color: transparent !important;
}

/* Super Search Input - design: bg-dark-bg, border white/8, placeholder text-dark-text-muted, focus ring */
.supersearch-input :deep(.input-field) {
  padding-left: 2.25rem; /* pl-9 for search icon */
  padding-right: 2.5rem; /* space for clear button */
  background-color: var(--color-dark-bg);
  color: var(--color-text);
  font-size: 0.875rem;
  border-radius: 0.5rem;
}
.supersearch-input :deep(.input-field::placeholder) {
  color: var(--color-text-muted); /* placeholder:text-dark-text-muted */
}
.supersearch-input :deep(.input-field:focus) {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}
:global(.dark) .supersearch-input :deep(.input-field) {
  border-color: rgba(255, 255, 255, 0.08);
}
/* Hide native browser search clear button */
.supersearch-input :deep(input[type="search"]::-webkit-search-cancel-button) {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}

</style>
